<template>
  <div class="inline-csat">
    <!-- LIKE BUTTONS -->
    <aha-antd-button
      v-if="!like && !dislike"
      :key="'inline-csat-like-button'"
      name="inline-csat-like-button"
      v-aha-emit-action="data"
      class="cursor-pointer"
      :class="{'is-invisible': isButtonInvisible}"
      v-tooltip="tooltip?.like"
      @click="onThumbUpClicked"
      variant="tertiary"
      size="sm"
    >
      <aha-icon name="thumb-up" />
    </aha-antd-button>

    <aha-antd-button
      v-if="like"
      :key="'inline-csat-un-like-button'"
      name="inline-csat-un-like-button"
      v-aha-emit-action="data"
      class="cursor-pointer"
      :class="{'is-invisible': isButtonInvisible}"
      v-tooltip="tooltip?.like"
      @click="onThumbUpClicked"
      variant="tertiary"
      size="sm"
      :active="true"
    >
      <aha-icon name="thumb-up" />
    </aha-antd-button>

    <!-- DISLIKE BUTTONS -->
    <div class="thumb-down__wrapper" :class="{'is-invisible': isButtonInvisible}">
      <aha-antd-button
        v-if="!like && !dislike"
        :key="'inline-csat-dislike-button'"
        name="inline-csat-dislike-button"
        v-aha-emit-action="data"
        class="cursor-pointer"
        v-tooltip="tooltip?.dislike"
        @click="onThumbDownClicked"
        variant="tertiary"
        size="sm"
      >
        <aha-icon name="thumb-up" class="transform rotate-180" />
      </aha-antd-button>

      <div v-if="dislike" class="flex items-center gap-0.5">
        <aha-antd-button
          :key="'inline-csat-un-dislike-button'"
          name="inline-csat-un-dislike-button"
          v-aha-emit-action="data"
          class="cursor-pointer"
          v-tooltip="tooltip?.dislike"
          @click="onThumbDownClicked"
          variant="tertiary"
          size="sm"
          :active="true"
        >
          <aha-icon name="thumb-up" class="transform rotate-180" />
        </aha-antd-button>

        <!-- FEEDBACK BUTTON -->
        <div>
          <div :name="feedbackTooltipName">
            <aha-antd-button
              :key="'inline-csat-feedback-button'"
              name="inline-csat-feedback-button"
              v-aha-emit-action="data"
              :disabled="isFeedbackSubmitted"
              size="sm"
              variant="text"
              @click="onFeedbackButtonClicked"
            >
              {{ $i18nPresenter.t('Feedback') }}
            </aha-antd-button>
          </div>

          <!-- FEEDBACK TOOLTIP + TEXTAREA -->
          <div v-if="!isFeedbackSubmitted">
            <aha-toggle-tip
              trigger="click"
              :to="feedbackTooltipName"
              theme="light"
              ref="feedback-toggle-tip"
            >
              <template #custom-content>
                <aha-textarea
                  :placeholder="translatedPlaceholder"
                  id="inline-csat-feedback"
                  v-model="feedback"
                  class="inline-csat-textarea"
                />
                <div class="w-full mb-2 mx-2">
                  <aha-antd-button
                    :key="feedback"
                    :variant="'secondary'"
                    name="inline-csat-submit-button"
                    v-aha-emit-action="{user_input: feedback, ...data}"
                    :disabled="!feedback?.length"
                    @click="onSubmitButtonClicked"
                  >
                    {{ $i18nPresenter.t('Send') }}
                  </aha-antd-button>
                </div>
              </template>
            </aha-toggle-tip>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import utils from '@/util'

export default {
  props: {
    id: {
      type: String,
      default: '',
    },
    data: {
      type: Object,
      default: () => {},
    },
    enableFeedback: {
      type: Boolean,
      default: false,
    },
    hoverToShow: {
      type: Boolean,
      default: false,
    },
    tooltip: {
      type: Object,
      default: () => {},
    },
    placeholder: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      feedback: '',
    }
  },

  computed: {
    ...mapGetters('inlineCSAT', ['getLike', 'getDislike', 'getIsFeedbackSubmitted']),

    feedbackTooltipName() {
      return `feedback-button-${this.id}`
    },

    translatedPlaceholder() {
      return this.$i18nPresenter.t(this.placeholder) || this.$i18nPresenter.t('How can we improve? Let us know!')
    },

    isFeedbackSubmitted() {
      return this.getIsFeedbackSubmitted(this.id)
    },

    like() {
      return this.getLike(this.id)
    },

    dislike() {
      return this.getDislike(this.id)
    },

    isButtonInvisible() {
      return this.hoverToShow && !this.like && !this.dislike
    },
  },

  methods: {
    ...mapActions('inlineCSAT', ['setDataAction']),

    onThumbUpClicked() {
      this.setDataAction({
        id: this.id,
        data: {
          like: !this.like,
          isFeedbackSubmitted: false,
        },
      })
    },

    onThumbDownClicked() {
      this.setDataAction({
        id: this.id,
        data: {
          dislike: !this.dislike,
          isFeedbackSubmitted: false,
        },
      })
    },

    onSubmitButtonClicked() {
      this.$refs['feedback-toggle-tip']?.hide()
      this.setDataAction({
        id: this.id,
        data: {
          isFeedbackSubmitted: true,
        },
      })
      this.feedback = ''
      utils.showToastSuccess(this.$i18nPresenter.t('Thank you for your feedback!'))
    },

    async onFeedbackButtonClicked() {
      await utils.sleep(200)
      const input = document.getElementById('inline-csat-feedback')
      const textarea = input.querySelector('textarea')
      textarea?.focus()
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";

.inline-csat {
  @apply flex items-center mb-1 gap-0.5;
}
.is-invisible {
  @apply opacity-0;
  transition: 0.2s ease-in-out;
}
.inline-csat-textarea {
  @apply mb-4 text-secondary-100 text-sm;
  &::v-deep {
    .aha-textarea {
      @apply mt-2 mb-3 mx-2;
      max-width: calc($toggle-tip-max-width - 32px);
    }
  }
}
</style>
